import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Select } from "@/components/base/Field";
import { EmptyState, ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSchoolYear } from "@/contexts/SchoolYearContext";
import { useAsync } from "@/hooks/useAsync";
import { getClasses } from "@/services/academicService";
import {
  canTeach,
  deleteEvaluation,
  getEvaluations,
  getGradeScales,
  getPeriods,
  getTeachable,
  type Evaluation,
} from "@/services/gradeService";
import type { SchoolClass } from "@/types";
import { formatDate } from "@/utils/format";
import EvaluationFormModal from "./components/EvaluationFormModal";
import GradeEntryPanel from "./components/GradeEntryPanel";

async function loadGradesPage(schoolYear: string, canReadClasses: boolean) {
  const [periods, allClasses, scales, teachable] = await Promise.all([
    getPeriods(schoolYear),
    canReadClasses ? getClasses() : null,
    getGradeScales(),
    getTeachable(),
  ]);
  // Compte enseignant : pas d'accès aux classes, ses classes viennent de ses affectations.
  const classes: Array<Pick<SchoolClass, "id" | "name" | "cycleId">> =
    allClasses ?? teachable.assignments.map((a) => ({ id: a.classId, name: a.className, cycleId: a.cycleId }));
  return { periods, classes, scales, teachable };
}

/** Période qui contient aujourd'hui, sinon la dernière commencée, sinon la première. */
function currentPeriodId(periods: { id: string; startDate: string; endDate: string }[]): string {
  const today = new Date().toISOString().slice(0, 10);
  const current = periods.find((p) => p.startDate <= today && today <= p.endDate);
  const started = [...periods].reverse().find((p) => p.startDate <= today);
  return (current ?? started ?? periods[0])?.id ?? "";
}

/**
 * Notes : les évaluations d'une classe pour une période (chacune porte sur toutes les matières de la classe),
 * et la saisie des notes matière par matière.
 */
export default function GradesPage() {
  const { notify } = useToast();
  const { can } = useAuth();
  const { consulted } = useSchoolYear();
  const { data, loading, error, reload } = useAsync(() => loadGradesPage(consulted, can("classes:read")), [consulted, can]);

  const [periodId, setPeriodId] = useState("");
  const [classId, setClassId] = useState("");
  const [evaluations, setEvaluations] = useState<Evaluation[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Evaluation | null>(null);
  const [grading, setGrading] = useState<{ evaluation: Evaluation; subjectId: string } | null>(null);
  /** Des notes sont tapées dans la saisie ouverte mais pas encore enregistrées. */
  const [gradingDirty, setGradingDirty] = useState(false);
  const [toDelete, setToDelete] = useState<Evaluation | null>(null);

  const teachable = data?.teachable ?? null;
  // Un enseignant ne voit d'abord que ses classes ; la direction voit toutes les classes.
  const classes = useMemo(() => {
    const all = data?.classes ?? [];
    if (!teachable || teachable.all || teachable.assignments.length === 0) return all;
    const mine = new Set(teachable.assignments.map((a) => a.classId));
    return all.filter((c) => mine.has(c.id));
  }, [data, teachable]);
  const schoolClass = classes.find((c) => c.id === classId);
  const period = data?.periods.find((p) => p.id === periodId);
  const scale = data?.scales.find((s) => s.cycleId === schoolClass?.cycleId)?.gradeScale ?? 20;
  const open = !!period && !period.isLocked;
  const canManage = can("grades:update") && open;

  useEffect(() => {
    if (!data) return;
    setPeriodId((prev) => (data.periods.some((p) => p.id === prev) ? prev : currentPeriodId(data.periods)));
  }, [data]);
  useEffect(() => {
    setClassId((prev) => (classes.some((c) => c.id === prev) ? prev : (classes[0]?.id ?? "")));
  }, [classes]);

  const loadEvaluations = useCallback(() => {
    if (!periodId || !classId) {
      setEvaluations([]);
      return;
    }
    setEvaluations(null);
    getEvaluations(periodId, classId)
      .then(setEvaluations)
      .catch((err: unknown) => {
        setEvaluations([]);
        notify(err instanceof Error ? err.message : "Chargement impossible.", "error");
      });
  }, [periodId, classId, notify]);
  useEffect(loadEvaluations, [loadEvaluations]);

  const closeGrading = useCallback(() => {
    setGrading(null);
    setGradingDirty(false);
  }, []);
  const openGrading = (evaluation: Evaluation, subjectId?: string) => {
    const first = evaluation.subjects.find((s) => canTeach(teachable, evaluation.classId, s.subjectId)) ?? evaluation.subjects[0];
    if (!first) return notify("Aucune matière n'est attribuée à cette classe (page Classes).", "error");
    const next = subjectId ?? first.subjectId;
    if (grading?.evaluation.id === evaluation.id && grading.subjectId === next) return;
    if (gradingDirty && !window.confirm("Les notes saisies ne sont pas enregistrées. Ouvrir une autre saisie quand même ?")) return;
    setGradingDirty(false);
    setGrading({ evaluation, subjectId: next });
  };

  // Changer de période ou de classe ferme la saisie en cours (elle concerne une autre classe).
  useEffect(() => {
    setGrading(null);
    setGradingDirty(false);
  }, [periodId, classId]);

  if (loading && !data) return <LoadingState label="Chargement des notes..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  if (data.periods.length === 0) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Notes" subtitle={`Année ${consulted}`} icon="ri-edit-box-line" />
        <Card>
          <EmptyState
            icon="ri-calendar-schedule-line"
            title="Les périodes de notes ne sont pas encore créées"
            message={`Découpez d'abord l'année ${consulted} en trimestres ou en semestres (Paramètres › Notes et périodes).`}
            action={
              can("grading-periods:update") ? (
                <Link to="/dashboard/settings?tab=grading">
                  <Button icon="ri-settings-3-line">Créer les périodes</Button>
                </Link>
              ) : undefined
            }
          />
        </Card>
      </div>
    );
  }

  const defaultDate = (() => {
    const today = new Date().toISOString().slice(0, 10);
    if (!period) return today;
    return today < period.startDate ? period.startDate : today > period.endDate ? period.endDate : today;
  })();

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Notes"
        subtitle={`Évaluations et notes — année ${consulted}`}
        icon="ri-edit-box-line"
        actions={
          can("report-cards:read") ? (
            <Link to="/dashboard/report-cards">
              <Button variant="outline" icon="ri-file-chart-line">
                Voir les bulletins
              </Button>
            </Link>
          ) : undefined
        }
      />

      {teachable && !teachable.all && teachable.assignments.length === 0 && can("grades:create") && (
        <p className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          Aucune classe ne vous est affectée, ou votre compte n'est relié à aucune fiche enseignant. Demandez à
          l'administrateur de vérifier votre fiche (Enseignants › Modifier).
        </p>
      )}

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select label="Période" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            {data.periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.isLocked ? " (verrouillé)" : ""}
              </option>
            ))}
          </Select>
          <Select label="Classe" value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.length === 0 && <option value="">Aucune classe</option>}
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        {period && (
          <p className="mt-3 text-xs text-foreground-500">
            {period.name} : du {formatDate(period.startDate)} au {formatDate(period.endDate)} · notes sur {scale} · la note du
            trimestre dans une matière est la moyenne de ses évaluations
            {period.isLocked && (
              <span className="ml-2">
                <Badge tone="secondary" icon="ri-lock-2-line">
                  Verrouillé par {period.lockedByName}
                </Badge>
              </span>
            )}
          </p>
        )}
      </Card>

      <Card padded={false}>
        <div className="flex flex-col gap-2 border-b border-background-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold font-heading text-foreground-950">
              Évaluations {schoolClass ? `— ${schoolClass.name}` : ""} {period ? `· ${period.name}` : ""}
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              Chaque évaluation porte sur toutes les matières de la classe ; choisissez la matière au moment de saisir.
            </p>
          </div>
          {canManage && schoolClass && (
            <Button
              icon="ri-add-line"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Nouvelle évaluation
            </Button>
          )}
        </div>

        {evaluations === null ? (
          <LoadingState label="Chargement des évaluations..." />
        ) : evaluations.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon="ri-file-list-3-line"
              title="Aucune évaluation"
              message={
                canManage
                  ? "Créez la première évaluation de la période (ex. « Évaluation d'octobre ») : elle servira pour toutes les matières."
                  : "La direction ou le secrétariat crée les évaluations ; vous y saisirez ensuite les notes de vos matières."
              }
            />
          </div>
        ) : (
          <ul className="divide-y divide-background-200">
            {evaluations.map((e) => {
              const done = e.subjects.filter((s) => e.studentCount > 0 && s.graded >= e.studentCount).length;
              return (
                <li
                  key={e.id}
                  className={`p-4 transition-colors ${grading?.evaluation.id === e.id ? "bg-primary-50/60 shadow-[inset_3px_0_0] shadow-primary-500" : ""}`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground-900">{e.title}</p>
                      <p className="text-xs text-foreground-500">
                        {formatDate(e.date)} · notée sur {e.maxScore} · {done}/{e.subjects.length} matière
                        {e.subjects.length > 1 ? "s" : ""} complète{done > 1 ? "s" : ""} · créée par {e.createdByName}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      <Button size="sm" icon={open ? "ri-edit-box-line" : "ri-eye-line"} onClick={() => openGrading(e)}>
                        {open ? "Saisir les notes" : "Voir les notes"}
                      </Button>
                      {canManage && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(e);
                              setFormOpen(true);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 cursor-pointer"
                            aria-label="Modifier l'évaluation"
                          >
                            <i className="ri-pencil-line"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => setToDelete(e)}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 cursor-pointer"
                            aria-label="Supprimer l'évaluation"
                          >
                            <i className="ri-delete-bin-6-line"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.subjects.map((s) => {
                      const complete = e.studentCount > 0 && s.graded >= e.studentCount;
                      const mine = canTeach(teachable, e.classId, s.subjectId);
                      return (
                        <button
                          key={s.subjectId}
                          type="button"
                          onClick={() => openGrading(e, s.subjectId)}
                          title={mine && open ? "Saisir les notes de cette matière" : "Voir les notes de cette matière"}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer transition-colors ${
                            complete
                              ? "border-primary-200 bg-primary-50 text-primary-800 hover:bg-primary-100"
                              : s.graded > 0
                                ? "border-secondary-200 bg-secondary-50 text-secondary-800 hover:bg-secondary-100"
                                : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                          } ${mine && !teachable?.all ? "ring-1 ring-primary-300" : ""} ${
                            grading?.evaluation.id === e.id && grading.subjectId === s.subjectId ? "!border-primary-600 font-semibold" : ""
                          }`}
                          aria-pressed={grading?.evaluation.id === e.id && grading.subjectId === s.subjectId}
                        >
                          <i className={complete ? "ri-checkbox-circle-fill" : s.graded > 0 ? "ri-time-line" : "ri-checkbox-blank-circle-line"}></i>
                          {s.name}
                          <span className="tabular-nums opacity-70">
                            {s.graded}/{e.studentCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <GradeEntryPanel
        evaluation={grading?.evaluation ?? null}
        initialSubjectId={grading?.subjectId ?? ""}
        teachable={teachable}
        onClose={closeGrading}
        onSaved={loadEvaluations}
        onDirtyChange={setGradingDirty}
        onSubjectChange={(subjectId) => grading && openGrading(grading.evaluation, subjectId)}
      />

      {schoolClass && (
        <EvaluationFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={loadEvaluations}
          editing={editing}
          periodId={periodId}
          classId={classId}
          className={schoolClass.name}
          gradeScale={scale}
          defaultDate={defaultDate}
        />
      )}

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer l'évaluation"
        message={`Supprimer « ${toDelete?.title} » et toutes ses notes, dans toutes les matières ? Les moyennes du trimestre seront recalculées sans elle.`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await deleteEvaluation(toDelete.id);
            if (grading?.evaluation.id === toDelete.id) closeGrading();
            notify("Évaluation supprimée.", "info");
            loadEvaluations();
          } catch (err) {
            notify(err instanceof Error ? err.message : "Suppression impossible.", "error");
          }
        }}
      />
    </div>
  );
}
